import {
  applyCustomerContextHeaders,
  clearCustomerContextHeaders,
} from "common/HttpClient/CustomerContextHeaders";
import { isRevokedCustomerAccessStatus } from "common/Constants/AccessStatus.constants";

export const SELECTED_CUSTOMER_KEY = "selected_customer";

const PLATFORM_ADMIN_HOME_PATHS = new Set(["/", "/home"]);

export const isPlatformAdminHomePath = (pathname = "") => {
  const normalized = String(pathname ?? "").replace(/\/+$/, "") || "/";
  return PLATFORM_ADMIN_HOME_PATHS.has(normalized);
};

export const parseStoredSelectedCustomer = () => {
  const raw = localStorage.getItem(SELECTED_CUSTOMER_KEY);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const hasStoredSelectedCustomer = () => {
  const parsed = parseStoredSelectedCustomer();
  if (!parsed || typeof parsed !== "object") {
    return false;
  }
  const id = parsed.id ?? parsed.customerId;
  const customerCode =
    parsed.customerCode;
  return (
    id != null &&
    String(id).trim() !== "" &&
    customerCode != null &&
    String(customerCode).trim() !== ""
  );
};

export const filterSelectableCustomerAccessRows = (rows = []) =>
  (rows ?? []).filter((row) => isSelectableCustomerAccessRow(row) && row.canSwitch);

export const isSelectableCustomerAccessRow = (row) =>
  !isRevokedCustomerAccessStatus(row?.accessStatus);

export const findCustomerAccessRow = (rows, customerInfo) => {
  if (!customerInfo || !rows?.length) {
    return null;
  }
  const infoCode =
    customerInfo.customerCode;
  return (
    rows.find(
      (row) =>
        String(row.id ?? row.customerId) ===
          String(customerInfo.id ?? customerInfo.customerId) ||
        String(row.customerCode) === String(infoCode ?? "")
    ) ?? null
  );
};

export const buildStoredCustomerInfo = (stored, accessRow, me) => {
  const id =
    stored?.id ??
    stored?.customerId ??
    accessRow?.id ??
    accessRow?.customerId ??
    me?.activeCustomerId;
  const customerCodeRaw =
    stored?.customerCode ??
    accessRow?.customerCode ??
    me?.activeCustomerCode;
  const customerCode = String(customerCodeRaw).trim();

  return {
    id,
    name:
      stored?.name ??
      accessRow?.name ??
      me?.activeCustomerName ??
      "",
    customerCode,
    timeZone: stored?.timeZone ?? accessRow?.timeZone,
  };
};

export const writeStoredSelectedCustomer = (customerInfo, userId) => {
  if (!customerInfo) {
    return;
  }
  localStorage.setItem(SELECTED_CUSTOMER_KEY, JSON.stringify(customerInfo));
  const resolvedUserId =
    userId != null && String(userId).trim() !== "" ? String(userId).trim() : null;
  if (customerInfo.customerCode && resolvedUserId) {
    applyCustomerContextHeaders(resolvedUserId, customerInfo.customerCode);
  }
};

export const clearStoredSelectedCustomer = ({ dispatchEvents = true } = {}) => {
  localStorage.removeItem(SELECTED_CUSTOMER_KEY);
  if (dispatchEvents && typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("customerReset", { detail: null }));
    window.dispatchEvent(
      new CustomEvent("customerChanged", { detail: null })
    );
  }
};

export const resetPlatformAdminCustomerSelection = () => {
  clearStoredSelectedCustomer();
  clearCustomerContextHeaders();
};

export const resolveStoredCustomerFromAccessList = (rows, me) => {
  const stored = parseStoredSelectedCustomer();
  if (!stored) {
    return null;
  }
  const matchedRow = findCustomerAccessRow(rows, stored);
  if (!matchedRow || isRevokedCustomerAccessStatus(matchedRow.accessStatus)) {
    return null;
  }
  return buildStoredCustomerInfo(stored, matchedRow, me);
};
